import React, { useState, useEffect } from "react";
import {
  Users,
  Plus,
  Search,
  Edit,
  Trash2,
  MapPin,
  Phone,
  Mail,
  FileText,
  Calendar,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Dialog, DialogContent } from "../ui/dialog";
import ClientFormModal from "./ClientFormModal";
import clientService from "../../services/clientService";
import { showError, showInfo } from "@/store/errorStore";

/**
 * ClientsManagement Component
 * Gerenciamento completo de clientes
 */
const ClientsManagement = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [savingClient, setSavingClient] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingClient, setDeletingClient] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  /**
   * Carregar todos os dados
   */
  const loadData = async () => {
    setLoading(true);
    try {
      await fetchClients();
    } catch (error) {
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Buscar todos os clientes
   */
  const fetchClients = async () => {
    try {
      const data = await clientService.getAllClients();
      setClients(Array.isArray(data) ? data : []);
    } catch (error) {
      // Modal global já exibido pelo interceptor
      setClients([]);
    }
  };

  /**
   * Abrir modal para criar novo cliente
   */
  const handleCreate = () => {
    setEditingClient(null);
    setShowModal(true);
  };

  /**
   * Abrir modal para editar cliente
   */
  const handleEdit = (client) => {
    setEditingClient(client);
    setShowModal(true);
  };

  /**
   * Salvar cliente (criar ou atualizar)
   */
  const handleSave = async (clientData) => {
    setSavingClient(true);
    try {
      if (editingClient) {
        await clientService.updateClient(editingClient.id, clientData);
        showInfo("Cliente atualizado com sucesso!", "Sucesso");
      } else {
        await clientService.createClient(clientData);
        showInfo("Cliente criado com sucesso!", "Sucesso");
      }
      setShowModal(false);
      await fetchClients();
    } catch (error) {
      // Erros HTTP já abrem o modal global; aqui só falha local
      if (!error.response) {
        showError(error.message || "Erro ao salvar cliente", "Erro ao Salvar");
      }
    } finally {
      setSavingClient(false);
    }
  };

  /**
   * Confirmar exclusão
   */
  const confirmDelete = (client) => {
    // A API remove o cliente pelo CPF (DELETE /clients/{cpf})
    if (!client?.cpf) return;
    setDeletingClient(client);
    setShowDeleteDialog(true);
  };

  /**
   * Deletar cliente
   */
  const handleDelete = async () => {
    if (!deletingClient) return;

    setLoadingModal(true);
    try {
      await clientService.deleteClient(deletingClient.cpf);
      showInfo("Cliente excluído com sucesso!", "Sucesso");
      setShowDeleteDialog(false);
      await fetchClients();
    } catch (error) {
      // Modal global já exibido pelo interceptor
    } finally {
      setLoadingModal(false);
      setDeletingClient(null);
    }
  };

  /**
   * Filtrar clientes
   */
  const filteredClients = clients.filter((client) => {
    const query = searchQuery.toLowerCase();
    return (
      client.name?.toLowerCase().includes(query) ||
      client.cpf?.includes(query) ||
      client.email?.toLowerCase().includes(query) ||
      client.contact?.includes(query)
    );
  });

  /**
   * Estatísticas
   */
  const stats = {
    total: clients.length,
    withEmail: clients.filter((c) => c.email).length,
    withAddress: clients.filter((c) => !!c.address)
      .length,
  };

  return (
    <div className="space-y-6">
      {/* Título e Botão de Criar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Users className="h-7 w-7" />
            Gestão de Clientes
          </h2>
          <p className="text-gray-600 mt-1">
            Gerencie todos os clientes cadastrados no sistema
          </p>
        </div>
        <Button
          onClick={handleCreate}
          className="bg-[#1b5e20] hover:bg-[#144919]"
        >
          <Plus className="h-4 w-4 mr-2" />
          Novo Cliente
        </Button>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total de Clientes
            </CardTitle>
            <Users className="h-4 w-4 text-gray-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-gray-600 mt-1">Cadastrados no sistema</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Com Email</CardTitle>
            <Mail className="h-4 w-4 text-gray-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.withEmail}</div>
            <p className="text-xs text-gray-600 mt-1">
              {stats.total > 0
                ? `${Math.round((stats.withEmail / stats.total) * 100)}%`
                : "0%"}{" "}
              do total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Com Endereço</CardTitle>
            <MapPin className="h-4 w-4 text-gray-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.withAddress}</div>
            <p className="text-xs text-gray-600 mt-1">
              {stats.total > 0
                ? `${Math.round((stats.withAddress / stats.total) * 100)}%`
                : "0%"}{" "}
              do total
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros e Busca */}
      <Card>
        <CardHeader>
          <CardTitle>Clientes Cadastrados</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Buscar por nome, CPF, email ou telefone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-primary mb-4"></div>
                <p className="text-gray-600 font-medium">
                  Carregando clientes...
                </p>
              </div>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">
                {searchQuery
                  ? "Nenhum cliente encontrado"
                  : "Nenhum cliente cadastrado"}
              </p>
              <p className="text-gray-500 text-sm mt-2">
                {searchQuery
                  ? "Tente buscar com outros termos"
                  : "Clique em 'Novo Cliente' para começar"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>CPF</TableHead>
                    <TableHead>Telefone</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Endereços</TableHead>
                    <TableHead>Cadastro</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.map((client) => (
                    <TableRow key={client.id}>
                      <TableCell className="font-medium">
                        {client.name}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-gray-400" />
                          {clientService.formatCPF(client.cpf)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-gray-400" />
                          {clientService.formatPhone(client.contact)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {client.email ? (
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-gray-400" />
                            <span className="text-sm">{client.email}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {client.address ? (
                          <Badge
                            variant="secondary"
                            title={[
                              client.address.logradouro,
                              client.address.number,
                              client.address.bairro,
                              client.address.localidade,
                            ]
                              .filter(Boolean)
                              .join(", ")}
                          >
                            <MapPin className="h-3 w-3 mr-1" />
                            {client.address.localidade || "Com endereço"}
                            {client.address.uf ? `/${client.address.uf}` : ""}
                          </Badge>
                        ) : (
                          <span className="text-gray-400 text-sm">
                            Sem endereço
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">
                            {clientService.formatDate(client.created_at)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(client)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => confirmDelete(client)}
                            disabled={!client.cpf}
                            title={
                              client.cpf
                                ? "Excluir cliente"
                                : "Cliente sem CPF não pode ser excluído"
                            }
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Formulário */}
      <ClientFormModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSave}
        client={editingClient}
        isSaving={savingClient}
      />

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o cliente{" "}
              <strong>{deletingClient?.name}</strong>?
              <br />
              <br />
              Esta ação não poderá ser desfeita e todos os dados do cliente
              serão removidos permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loadingModal}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={loadingModal}
              className="bg-red-600 hover:bg-red-700"
            >
              {loadingModal ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Loading Modal */}
      <Dialog open={loadingModal}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center justify-center py-6">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-primary mb-4"></div>
            <p className="text-gray-600 font-medium">Processando...</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsManagement;
